import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../services/api';
import './ProjectExpenses.css';

const ProjectExpenses = ({ projectId, onUpdate }) => {
  const [expenses, setExpenses] = useState([]);
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    if (projectId) {
      loadExpenses();
      loadBudgetSummary();
    }
  }, [projectId]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getExpenses(projectId);
      setExpenses(response.data);
    } catch (error) {
      console.error('Error loading expenses:', error);
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadBudgetSummary = async () => {
    try {
      const response = await projectsAPI.getBudgetSummary(projectId);
      setBudgetSummary(response.data);
    } catch (error) {
      console.error('Error loading budget summary:', error);
    }
  };

  const handleCreateExpense = () => {
    setEditingExpense(null);
    setFormData({
      description: '',
      amount: '',
      category: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setShowForm(true);
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    // Format date properly - handle both Date objects and ISO strings
    let dateValue = new Date().toISOString().split('T')[0];
    if (expense.date) {
      if (typeof expense.date === 'string') {
        dateValue = expense.date.split('T')[0]; // Extract date part from ISO string
      } else if (expense.date instanceof Date) {
        dateValue = expense.date.toISOString().split('T')[0];
      }
    }
    
    setFormData({
      description: expense.description || '',
      amount: expense.amount ? parseFloat(expense.amount).toString() : '',
      category: expense.category || '',
      date: dateValue,
      notes: expense.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.amount) {
      alert('Please fill in description and amount');
      return;
    }

    try {
      // Validate amount
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount greater than 0');
        return;
      }
      
      // Validate date format
      if (!formData.date) {
        alert('Please select a date');
        return;
      }
      
      const data = {
        project_id: projectId,
        description: formData.description.trim(),
        amount: amount,
        category: formData.category?.trim() || null,
        date: formData.date,
        notes: formData.notes?.trim() || null,
      };

      if (editingExpense) {
        await projectsAPI.updateExpense(editingExpense.id, data);
      } else {
        await projectsAPI.createExpense(projectId, data);
      }

      setShowForm(false);
      setEditingExpense(null);
      loadExpenses();
      loadBudgetSummary();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      alert('Failed to save expense: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      await projectsAPI.deleteExpense(id);
      loadExpenses();
      loadBudgetSummary();
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      alert('Failed to delete expense');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const calculateTotal = () => {
    return expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
  };

  if (loading) {
    return <div className="expenses-loading">Loading expenses...</div>;
  }

  const totalExpenses = calculateTotal();
  const estimated = budgetSummary?.budget_estimated || null;
  const remaining = estimated ? estimated - totalExpenses : null;
  const progress = estimated && estimated > 0 ? (totalExpenses / estimated) * 100 : 0;

  return (
    <div className="project-expenses">
      {budgetSummary && (
        <div className="budget-summary-card">
          <h3>Budget Summary</h3>
          <div className="budget-summary-grid">
            {estimated && (
              <div className="budget-summary-item">
                <span className="budget-label">Estimated:</span>
                <span className="budget-value">{formatCurrency(estimated)}</span>
              </div>
            )}
            <div className="budget-summary-item">
              <span className="budget-label">Actual Spent:</span>
              <span className="budget-value spent">{formatCurrency(totalExpenses)}</span>
            </div>
            {remaining !== null && (
              <div className="budget-summary-item">
                <span className="budget-label">Remaining:</span>
                <span className={`budget-value ${remaining < 0 ? 'negative' : 'positive'}`}>
                  {formatCurrency(remaining)}
                </span>
              </div>
            )}
            <div className="budget-summary-item">
              <span className="budget-label">Total Expenses:</span>
              <span className="budget-value">{expenses.length}</span>
            </div>
          </div>
          {estimated && estimated > 0 && (
            <div className="budget-progress-container">
              <div className="budget-progress-bar">
                <div
                  className="budget-progress-fill"
                  style={{
                    width: `${Math.min(progress, 100)}%`,
                    backgroundColor: progress > 100 ? 'var(--error)' : progress > 80 ? 'var(--warning)' : 'var(--primary)',
                  }}
                />
              </div>
              <div className="budget-progress-text">
                {progress.toFixed(1)}% of budget used
              </div>
            </div>
          )}
        </div>
      )}

      <div className="expenses-header">
        <h3>Expenses ({expenses.length})</h3>
        <button className="btn-add" onClick={handleCreateExpense}>
          + Add Expense
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="expense-form">
          <h4>{editingExpense ? 'Edit Expense' : 'Add New Expense'}</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Description *</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                placeholder="e.g., Paint, Labor, Materials"
              />
            </div>
            <div className="form-group">
              <label>Amount ($) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="">Select Category</option>
                <option value="Materials">Materials</option>
                <option value="Labor">Labor</option>
                <option value="Permits">Permits</option>
                <option value="Tools">Tools</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="2"
              placeholder="Additional notes..."
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => {
              setShowForm(false);
              setEditingExpense(null);
            }}>
              Cancel
            </button>
            <button type="submit" className="btn-submit">
              {editingExpense ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {expenses.length === 0 ? (
        <div className="expenses-empty">
          <p>No expenses recorded yet.</p>
          <p>Click "+ Add Expense" to start tracking project costs.</p>
        </div>
      ) : (
        <div className="expenses-list">
          {expenses.map((expense) => (
            <div key={expense.id} className="expense-item">
              <div className="expense-main">
                <div className="expense-info">
                  <div className="expense-description">{expense.description}</div>
                  <div className="expense-meta">
                    {expense.category && (
                      <span className="expense-category">{expense.category}</span>
                    )}
                    <span className="expense-date">{formatDate(expense.date)}</span>
                  </div>
                  {expense.notes && (
                    <div className="expense-notes">{expense.notes}</div>
                  )}
                </div>
                <div className="expense-amount">
                  {formatCurrency(expense.amount)}
                </div>
              </div>
              <div className="expense-actions">
                <button
                  className="btn-edit-small"
                  onClick={() => handleEditExpense(expense)}
                  title="Edit"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button
                  className="btn-delete-small"
                  onClick={() => handleDelete(expense.id)}
                  title="Delete"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectExpenses;
